import { Loader2 } from 'lucide-react';

/**
 * The "reading your sources" view that REPLACES the tab switcher — §2.8 #97.
 *
 * Not a banner above the tabs: while the user's own sources are still being
 * read there is genuinely nothing to do in either tab. The Prompt tab has
 * nothing to edit (the lesson has not started building, so a prompt change
 * would race the build), and the Sources tab's add/remove controls are all
 * locked anyway. Showing two tabs whose every control is disabled reads as a
 * broken panel; showing one honest progress view reads as a wait.
 *
 * It reuses the sources LIST rather than inventing a progress bar, because the
 * list already renders per-source status and the failed-row retry — the two
 * things a user watching an ingest actually wants. A lesson with no visible
 * sources (synthetic, or every row filtered out) has nothing to show there, so
 * it gets a neutral line instead of an empty scroll area.
 *
 * Extracted from `SourcesPanel` in Phase 11 — behaviour-preserving, same markup,
 * and the gate (`sourcesIngesting`) stays in `lib/sources/lockState` where it is
 * unit-tested.
 */
export interface SourcesIngestingViewProps {
  /** The panel's source list, when there is one to show. */
  children?: React.ReactNode;
  /** False for a synthetic lesson — nothing user-visible to render progress for. */
  hasVisibleSources: boolean;
}

export function SourcesIngestingView({
  children,
  hasVisibleSources,
}: SourcesIngestingViewProps) {
  return (
    <div className='flex h-full min-h-0 flex-col'>
      <div className='flex-shrink-0 px-3 pt-3'>
        <div className='flex items-center gap-2 text-sm font-medium text-foreground'>
          <Loader2 className='h-4 w-4 animate-spin text-brand-500 motion-reduce:animate-none dark:text-primary' />
          Reading your sources…
        </div>
        <p className='mt-1 text-xs text-muted-foreground'>
          We&apos;re going through your material. Your lesson starts building
          once this finishes.
        </p>
      </div>
      {hasVisibleSources ? (
        <div className='min-h-0 flex-1'>{children}</div>
      ) : (
        <div className='flex flex-1 items-center justify-center px-6 text-center text-xs text-muted-foreground'>
          Preparing your lesson&apos;s material…
        </div>
      )}
    </div>
  );
}
